import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixCalendarEventsV3EventsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/events-3',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/_api/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
      {
        srcPath: '/_api/calendar/v3/ro/events',
        destPath: '/v3/events',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
    _: [
      {
        srcPath: '/_api/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/_api/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/_api/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/_api/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/_api/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/calendar/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/_api/calendar/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveWixCalendarParticipanteventsV3ParticipantEventsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/participant-events-3',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/calendar/v3/events/memberId',
        destPath: '/v3/events/memberId',
      },
      {
        srcPath: '/_api/calendar/v3/events/contactId',
        destPath: '/v3/events/contactId',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/calendar/v3/events/contactId',
        destPath: '/v3/events/contactId',
      },
      {
        srcPath: '/calendar/v3/events/memberId',
        destPath: '/v3/events/memberId',
      },
    ],
    _: [
      {
        srcPath: '/_api/calendar/v3/events/memberId',
        destPath: '/v3/events/memberId',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/calendar/v3/events/memberId',
        destPath: '/v3/events/memberId',
      },
      {
        srcPath: '/_api/calendar/v3/events/contactId',
        destPath: '/v3/events/contactId',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_calendar_events';

/**
 * Retrieves a list of events filtered by the participant's
 * [contact](https://dev.wix.com/docs/sdk/backend-modules/crm/contacts/introduction)
 * ID.
 *
 *
 * You can't set `toLocalDate` to a value that's more than a full year after
 * `fromLocalDate`.
 */
export function listEventsByContactId(
  payload: object
): RequestOptionsFactory<any> {
  function __listEventsByContactId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'GET' as any,
      methodFqn:
        'wix.calendar.participantevents.v3.ParticipantEventsService.ListEventsByContactId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarParticipanteventsV3ParticipantEventsServiceUrl({
        protoPath: '/v3/events/contactId/{contactId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'events.createdDate' },
              { path: 'events.updatedDate' },
              { path: 'events.start.utcDate' },
              { path: 'events.end.utcDate' },
              { path: 'events.adjustedStart.utcDate' },
              { path: 'events.adjustedEnd.utcDate' },
              { path: 'events.recurrenceRule.until.utcDate' },
              { path: 'events.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listEventsByContactId;
}

/**
 * Retrieves a list of events filtered by the participant's
 * [member](https://dev.wix.com/docs/sdk/backend-modules/members/members/introduction)
 * ID.
 *
 *
 * You can't set `toLocalDate` to a value that's more than a full year after
 * `fromLocalDate`.
 */
export function listEventsByMemberId(
  payload: object
): RequestOptionsFactory<any> {
  function __listEventsByMemberId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'GET' as any,
      methodFqn:
        'wix.calendar.participantevents.v3.ParticipantEventsService.ListEventsByMemberId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarParticipanteventsV3ParticipantEventsServiceUrl({
        protoPath: '/v3/events/memberId/{memberId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'events.createdDate' },
              { path: 'events.updatedDate' },
              { path: 'events.start.utcDate' },
              { path: 'events.end.utcDate' },
              { path: 'events.adjustedStart.utcDate' },
              { path: 'events.adjustedEnd.utcDate' },
              { path: 'events.recurrenceRule.until.utcDate' },
              { path: 'events.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listEventsByMemberId;
}

/** Retrieves an event. */
export function getEvent(payload: object): RequestOptionsFactory<any> {
  function __getEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'GET' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.GetEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events/{eventId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.start.utcDate' },
              { path: 'event.end.utcDate' },
              { path: 'event.adjustedStart.utcDate' },
              { path: 'event.adjustedEnd.utcDate' },
              { path: 'event.recurrenceRule.until.utcDate' },
              { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getEvent;
}

/** Retrieves a list of events by their IDs. */
export function listEvents(payload: object): RequestOptionsFactory<any> {
  function __listEvents({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'GET' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.ListEvents',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'events.createdDate' },
              { path: 'events.updatedDate' },
              { path: 'events.start.utcDate' },
              { path: 'events.end.utcDate' },
              { path: 'events.adjustedStart.utcDate' },
              { path: 'events.adjustedEnd.utcDate' },
              { path: 'events.recurrenceRule.until.utcDate' },
              { path: 'events.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listEvents;
}

/**
 * Creates a query to retrieve a list of events.
 *
 * The `queryEvents()` function builds a query to retrieve a list of events and returns an `EventsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/events-query-builder/find) function.
 *
 * You can refine the query by chaining `EventsQueryBuilder` functions onto the query. `EventsQueryBuilder` functions enable you to sort, filter, and control the results that `queryEvents()` returns.
 *
 * `queryEvents()` runs with the following `EventsQueryBuilder` default that you can override:
 *
 * + `limit` is `50`.
 * + Sorted by `event.start` in ascending order.
 *
 * The functions that are chained to `queryEvents()` are applied in the order they are called.
 *
 * The following `EventsQueryBuilder` functions are supported for the `queryEvents()` function. For a full description of the events object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/events-query-result/items) property in `EventsQueryResult`.
 *
 */
export function queryEvents(payload: object): RequestOptionsFactory<any> {
  function __queryEvents({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.QueryEvents',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'events.createdDate' },
              { path: 'events.updatedDate' },
              { path: 'events.start.utcDate' },
              { path: 'events.end.utcDate' },
              { path: 'events.adjustedStart.utcDate' },
              { path: 'events.adjustedEnd.utcDate' },
              { path: 'events.recurrenceRule.until.utcDate' },
              { path: 'events.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryEvents;
}

/**
 * Creates an event.
 *
 *
 * If you provide `recurrenceRule`, an event with `{"recurrenceType": "MASTER"}` is created. Then, you can't set `start.localDate` to a past date, though the time can be earlier on the same day. You can, however, create single-occurrence events for past dates.
 *
 * You can't create an event with `recurrenceType` set to `INSTANCE` or `EXCEPTION`. `INSTANCE` events are generated automatically based on the recurrence rule in the relevant `MASTER` event. `EXCEPTION` events are automatically created when you [update](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event) an `INSTANCE` event, which changes its `recurrenceType` to `EXCEPTION`.
 */
export function createEvent(payload: object): RequestOptionsFactory<any> {
  function __createEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldmask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'event.createdDate' },
          { path: 'event.updatedDate' },
          { path: 'event.start.utcDate' },
          { path: 'event.end.utcDate' },
          { path: 'event.adjustedStart.utcDate' },
          { path: 'event.adjustedEnd.utcDate' },
          { path: 'event.recurrenceRule.until.utcDate' },
          { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.CreateEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.start.utcDate' },
              { path: 'event.end.utcDate' },
              { path: 'event.adjustedStart.utcDate' },
              { path: 'event.adjustedEnd.utcDate' },
              { path: 'event.recurrenceRule.until.utcDate' },
              { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createEvent;
}

/**
 * Creates multiple events in bulk.
 *
 *
 * If you provide `recurrenceRule`, an event with `{"recurrenceType": "MASTER"}` is
 * created. Then, you can't set `start.localDate` to a past date, though the time can be
 * earlier on the same day. You can, however, create single-occurrence events for
 * past dates.
 *
 * You can't create an event with `recurrenceType` set to `INSTANCE` or `EXCEPTION`.
 * `INSTANCE` events are generated automatically based on the recurrence rule in the
 * relevant `MASTER` event. `EXCEPTION` events are automatically created when you
 * [update](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event)
 * an `INSTANCE` event, which changes its `recurrenceType` to `EXCEPTION`.
 */
export function bulkCreateEvent(payload: object): RequestOptionsFactory<any> {
  function __bulkCreateEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'events.fieldmask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'events.event.createdDate' },
          { path: 'events.event.updatedDate' },
          { path: 'events.event.start.utcDate' },
          { path: 'events.event.end.utcDate' },
          { path: 'events.event.adjustedStart.utcDate' },
          { path: 'events.event.adjustedEnd.utcDate' },
          { path: 'events.event.recurrenceRule.until.utcDate' },
          { path: 'events.event.recurrenceRule.adjustedUntil.utcDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.BulkCreateEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/bulk/events/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.start.utcDate' },
              { path: 'results.item.end.utcDate' },
              { path: 'results.item.adjustedStart.utcDate' },
              { path: 'results.item.adjustedEnd.utcDate' },
              { path: 'results.item.recurrenceRule.until.utcDate' },
              { path: 'results.item.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateEvent;
}

/**
 * Updates an event.
 *
 *
 * When updating an event with `{"recurrenceType": "INSTANCE"}`, the
 * `recurrenceType` automatically changes to `EXCEPTION`.
 *
 * When updating an event with `{"recurrenceType": "MASTER"}`:
 * + Past `INSTANCE` and `EXCEPTION` events remain unchanged.
 * + For future `INSTANCE` events, all relevant fields are updated.
 * + For future `EXCEPTION` events, only changes to the `inheritedFields` are
 * applied, while other fields remain unchanged.
 */
export function updateEvent(payload: object): RequestOptionsFactory<any> {
  function __updateEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldmask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'event.createdDate' },
          { path: 'event.updatedDate' },
          { path: 'event.start.utcDate' },
          { path: 'event.end.utcDate' },
          { path: 'event.adjustedStart.utcDate' },
          { path: 'event.adjustedEnd.utcDate' },
          { path: 'event.recurrenceRule.until.utcDate' },
          { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'PATCH' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.UpdateEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events/{event.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.start.utcDate' },
              { path: 'event.end.utcDate' },
              { path: 'event.adjustedStart.utcDate' },
              { path: 'event.adjustedEnd.utcDate' },
              { path: 'event.recurrenceRule.until.utcDate' },
              { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateEvent;
}

/**
 * Updates multiple events in bulk.
 *
 *
 * When updating an event with `{"recurrenceType": "INSTANCE"}`, the
 * `recurrenceType` automatically changes to `EXCEPTION`.
 *
 * When updating an event with `{"recurrenceType": "MASTER"}`:
 * + Past `INSTANCE` and `EXCEPTION` events remain unchanged.
 * + For future `INSTANCE` events, all relevant fields are updated.
 * + For future `EXCEPTION` events, only changes to the `inheritedFields` are
 * applied, while other fields remain unchanged.
 */
export function bulkUpdateEvent(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'events.fieldmask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'events.event.createdDate' },
          { path: 'events.event.updatedDate' },
          { path: 'events.event.start.utcDate' },
          { path: 'events.event.end.utcDate' },
          { path: 'events.event.adjustedStart.utcDate' },
          { path: 'events.event.adjustedEnd.utcDate' },
          { path: 'events.event.recurrenceRule.until.utcDate' },
          { path: 'events.event.recurrenceRule.adjustedUntil.utcDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.BulkUpdateEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/bulk/events/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.start.utcDate' },
              { path: 'results.item.end.utcDate' },
              { path: 'results.item.adjustedStart.utcDate' },
              { path: 'results.item.adjustedEnd.utcDate' },
              { path: 'results.item.recurrenceRule.until.utcDate' },
              { path: 'results.item.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateEvent;
}

/**
 * Restores the event's default values from the relevant schedule or `MASTER`
 * event.
 *
 *
 * You must provide `fields` to specify which `inheritedFields` are restored.
 *
 * For example, if you've created an event that takes place in a non-default
 * location with a custom title and capacity, but you want to reset only title
 * and capacity to default values without affecting the location, provide the
 * event ID and set `fields` to `["TITLE", "CAPACITY"]`.
 *
 * Refer to the [default values article](https://dev.wix.com/docs/sdk/backend-modules/calendar/default-values)
 * for more information.
 */
export function restoreEventDefaults(
  payload: object
): RequestOptionsFactory<any> {
  function __restoreEventDefaults({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.RestoreEventDefaults',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events/{eventId}/restore-defaults',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.start.utcDate' },
              { path: 'event.end.utcDate' },
              { path: 'event.adjustedStart.utcDate' },
              { path: 'event.adjustedEnd.utcDate' },
              { path: 'event.recurrenceRule.until.utcDate' },
              { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __restoreEventDefaults;
}

/**
 * Splits a recurring `MASTER` event into 2 separate `MASTER` events.
 *
 *
 * You must provide a `splitLocalDate` that's in the future and after the `start`
 * date of the next `INSTANCE` or `EXCEPTION` event in the series. Additionally,
 * there must be another `INSTANCE` or `EXCEPTION` event following this next event,
 * as the changes wouldn't affect any event without a subsequent occurrence.
 *
 * If you want to modify a `MASTER` event with the changes already applying to
 * the very next event, use
 * [updateEvent()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event)
 * instead, as this preserves past events and only modifies future events.
 *
 * The main consequences are:
 * - **The original `MASTER` event is shortened**. Its `recurrenceRule.until.localDate`
 * is updated to `end.localDate` of the latest `INSTANCE` or `EXCEPTION`
 * event starting before `splitLocalDate`. If an `INSTANCE` or `EXCEPTION` event
 * starts before but ends after `splitLocalDate`, `until.localDate` is set
 * to value that's later than `splitLocalDate`.
 * - **A new `MASTER` event is created**, starting from the first event that
 * begins after `splitLocalDate`.
 * - **Existing `INSTANCE` and `EXCEPTION` events that occur after the split are updated**.
 * Their `recurringEventId` is set to the ID of the newly created `MASTER` event.
 */
export function splitRecurringEvent(
  payload: object
): RequestOptionsFactory<any> {
  function __splitRecurringEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.SplitRecurringEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events/{recurringEventId}/split',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'updatedRecurringEventEndingBeforeSplit.createdDate' },
              { path: 'updatedRecurringEventEndingBeforeSplit.updatedDate' },
              { path: 'updatedRecurringEventEndingBeforeSplit.start.utcDate' },
              { path: 'updatedRecurringEventEndingBeforeSplit.end.utcDate' },
              {
                path: 'updatedRecurringEventEndingBeforeSplit.adjustedStart.utcDate',
              },
              {
                path: 'updatedRecurringEventEndingBeforeSplit.adjustedEnd.utcDate',
              },
              {
                path: 'updatedRecurringEventEndingBeforeSplit.recurrenceRule.until.utcDate',
              },
              {
                path: 'updatedRecurringEventEndingBeforeSplit.recurrenceRule.adjustedUntil.utcDate',
              },
              { path: 'newRecurringEventStartingFromSplit.createdDate' },
              { path: 'newRecurringEventStartingFromSplit.updatedDate' },
              { path: 'newRecurringEventStartingFromSplit.start.utcDate' },
              { path: 'newRecurringEventStartingFromSplit.end.utcDate' },
              {
                path: 'newRecurringEventStartingFromSplit.adjustedStart.utcDate',
              },
              {
                path: 'newRecurringEventStartingFromSplit.adjustedEnd.utcDate',
              },
              {
                path: 'newRecurringEventStartingFromSplit.recurrenceRule.until.utcDate',
              },
              {
                path: 'newRecurringEventStartingFromSplit.recurrenceRule.adjustedUntil.utcDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __splitRecurringEvent;
}

/** Cancels an event. */
export function cancelEvent(payload: object): RequestOptionsFactory<any> {
  function __cancelEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.CancelEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/events/{eventId}/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.start.utcDate' },
              { path: 'event.end.utcDate' },
              { path: 'event.adjustedStart.utcDate' },
              { path: 'event.adjustedEnd.utcDate' },
              { path: 'event.recurrenceRule.until.utcDate' },
              { path: 'event.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelEvent;
}

/** Cancels multiple events in bulk. */
export function bulkCancelEvent(payload: object): RequestOptionsFactory<any> {
  function __bulkCancelEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.calendar.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.calendar.events.v3.EventsService.BulkCancelEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCalendarEventsV3EventsServiceUrl({
        protoPath: '/v3/bulk/events/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.start.utcDate' },
              { path: 'results.item.end.utcDate' },
              { path: 'results.item.adjustedStart.utcDate' },
              { path: 'results.item.adjustedEnd.utcDate' },
              { path: 'results.item.recurrenceRule.until.utcDate' },
              { path: 'results.item.recurrenceRule.adjustedUntil.utcDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCancelEvent;
}
